/**
 * Outlook-specific parsers for SSE `tool_end` event outputs.
 *
 * Each function reads the raw `output` string from a backend tool's
 * tool_end event and returns the structured shape (email draft / search
 * results / calendar sources / NBA sources) for the chat UI to render.
 *
 * These belong in the Outlook integration folder because the input shapes
 * (EmailDraft, EmailSearchResult, calendar sources, NBA sources) are all
 * Outlook-specific — Word and PowerPoint will have their own parsers if
 * they ever need similar tool-output extraction.
 *
 * After the Slice-3 renderer migration these parsers go away entirely:
 * each renderer becomes an atomic frontend tool the LLM calls via
 * interrupt() rather than a hardcoded `tool === "X"` branch in useChat.
 */

import type { EmailDraft, EmailSearchResult, EmailSearchWindow } from "../../../src/integrations/email/types";

interface CalendarSource {
  id: string;
  subject: string;
  start: string;
  end: string;
  webLink: string;
  organizer: string;
}

export function tryParseDraft(output: string): EmailDraft | null {
  try {
    const parsed = JSON.parse(output);
    return (parsed.draft as EmailDraft) ?? null;
  } catch {
    return null;
  }
}

/**
 * AIVA-7020 — the optional `search_window` block on the envelope. Absent on turns
 * that searched nothing (the criteria-less gate, a thread-scoped reply check) and
 * on any older/replayed payload, so every field is defended: a malformed block
 * degrades to no disclosure rather than to a wrong one.
 */
function tryParseSearchWindow(block: unknown): EmailSearchWindow | undefined {
  if (!block || typeof block !== "object") return undefined;
  const raw = block as Record<string, unknown>;
  const outlookQuery = typeof raw["outlook_query"] === "string" ? raw["outlook_query"] : "";
  const saturated = raw["saturated"] === true;
  // Nothing to disclose and nowhere to hand off to — treat as absent so the
  // renderer has one thing to check rather than three.
  if (!saturated && !outlookQuery) return undefined;
  const searched = typeof raw["searched"] === "number" ? raw["searched"] : undefined;
  return { saturated, outlookQuery, ...(searched !== undefined && { searched }) };
}

export function tryParseSearchResults(
  output: string,
): { results: EmailSearchResult[]; querySummary: string; searchWindow?: EmailSearchWindow } | null {
  try {
    const parsed = JSON.parse(output);
    if (parsed.status === "search_results_ready" && Array.isArray(parsed.results)) {
      const searchWindow = tryParseSearchWindow(parsed.search_window);
      return {
        results: parsed.results as EmailSearchResult[],
        querySummary: (parsed.query_summary as string) || "Search results",
        ...(searchWindow && { searchWindow }),
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function tryParseListEmailsOutput(output: string): EmailSearchResult[] | null {
  try {
    const parsed = JSON.parse(output);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    const first = parsed[0] as Record<string, unknown>;
    if (!first || typeof first["id"] !== "string") return null;
    return parsed as EmailSearchResult[];
  } catch {
    return null;
  }
}

// AIVA-5166 — render the event end as a friendly local time in the card instead
// of the raw ISO ("Ends 2026-06-11T17:00:00"). new Date() resolves both shapes
// correctly: a naive ISO is treated as local wall-clock; a UTC ('Z'/offset) ISO
// is converted to local (which also fixes the card showing UTC after a save).
function formatEventEnds(endIso: string): string {
  if (!endIso) return "";
  // Only reformat genuinely ISO-datetime strings; pass anything else through
  // verbatim so unexpected shapes are never mangled into a garbage date.
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(endIso)) return `Ends ${endIso}`;
  try {
    const d = new Date(endIso);
    if (Number.isNaN(d.getTime())) return `Ends ${endIso}`;
    return `Ends ${d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })}`;
  } catch {
    return `Ends ${endIso}`;
  }
}

export function tryParseCalendarSources(output: string): EmailSearchResult[] | null {
  const match = /__CALENDAR_SOURCES__([\s\S]*?)__CALENDAR_SOURCES_END__/.exec(output);
  if (!match) return null;
  try {
    const raw = JSON.parse(match[1]!) as CalendarSource[];
    if (!Array.isArray(raw) || raw.length === 0) return null;
    return raw.map((src) => ({
      id: src.id,
      subject: src.subject,
      from: src.organizer,
      fromName: src.organizer,
      date: src.start,
      preview: formatEventEnds(src.end),
      hasAttachments: false,
      webLink: src.webLink,
    }));
  } catch {
    return null;
  }
}

export function tryParseEmailSources(
  output: string,
): { results: EmailSearchResult[]; querySummary: string } | null {
  const match = /__EMAIL_SOURCES__([\s\S]*?)__EMAIL_SOURCES_END__/.exec(output);
  if (!match) return null;
  try {
    const raw = JSON.parse(match[1]!) as Array<{
      id: string;
      subject: string;
      from: string;
      fromName?: string;
      date: string;
      preview: string;
      hasAttachments?: boolean;
      webLink: string;
      conversationId?: string;
    }>;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    const results: EmailSearchResult[] = raw.map((src) => ({
      id: src.id,
      subject: src.subject,
      from: src.from,
      fromName: src.fromName ?? src.from,
      date: src.date,
      preview: src.preview,
      hasAttachments: Boolean(src.hasAttachments),
      webLink: src.webLink,
      conversationId: src.conversationId,
    }));
    return { results, querySummary: "Referenced emails" };
  } catch {
    return null;
  }
}

export function tryParseNbaSources(
  output: string,
): { results: EmailSearchResult[]; querySummary: string } | null {
  const match = /__NBA_SOURCES__([\s\S]*?)__NBA_SOURCES_END__/.exec(output);
  if (!match) return null;
  try {
    const raw = JSON.parse(match[1]!) as Array<{
      id: string;
      subject: string;
      from: string;
      date: string;
      preview: string;
      webLink: string;
      conversationId?: string;
    }>;
    if (!Array.isArray(raw) || raw.length === 0) return null;
    const results: EmailSearchResult[] = raw.map((src) => ({
      id: src.id,
      subject: src.subject,
      from: src.from,
      fromName: src.from,
      date: src.date,
      preview: src.preview,
      hasAttachments: false,
      webLink: src.webLink,
      conversationId: src.conversationId,
    }));
    return { results, querySummary: "Referenced emails" };
  } catch {
    return null;
  }
}
